import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { usePollSessionForFeedback } from '@/Pages/DatasetSessionCompare/utils/usePollSession';
import {
  useDeep,
  useInput,
  usePlaygroundStore,
} from '@/Pages/Playground/PlaygroundHome/hooks/usePlaygroundStore';
import {
  formatRunOutput,
  getDatasetSplits,
  getExampleRunsFromPlaygroundState,
  getPlaygroundStateWithReplacedFetchedRuns,
  needsToFetchFeedbackKeys,
} from '@/Pages/Playground/utils/Playground.utils';
import { useEvaluatorStore } from '@/components/EvaluatorCrudPane/store/evaluatorStore';
import {
  useExampleRunsInfinite,
  useExamples,
  useRunsQuery,
} from '@/hooks/useSwr';
import { ExampleSchemaWithRunsAndOptionalFields } from '@/types/schema';

import { EVALUATOR_PLAYGROUND_BATCH_SIZE } from '../constants';
import { mergeExampleRuns } from '../utils/mergeExampleRuns';

export const useSortedPlaygroundExampleRuns = ({
  sessionId,
}: {
  sessionId?: string;
}) => {
  const setExamples = usePlaygroundStore((state) => state.setExamples);
  const [searchParams] = useSearchParams();
  const sortBy = searchParams.get('sort_by');
  const sortOrder = searchParams.get('sort_order');

  const [numRunsFetched, setNumRunsFetched] = useState(0);
  const [needsFetchFeedbackKeys, setNeedsFetchFeedbackKeys] = useState(false);
  const [shouldHighlightExamples, setShouldHighlightExamples] = useState(false);

  const loadingState = usePlaygroundStore((state) => state.loadingState);
  const evaluatorFeedbackKeys = usePlaygroundStore(
    (state) => state.evaluatorFeedbackKeys
  );
  const datasetId = usePlaygroundStore((state) => state.datasetId);

  const { isSessionFinished } = usePollSessionForFeedback(
    sessionId,
    evaluatorFeedbackKeys
  );
  const shouldFetchExamplesWithRuns = sessionId && sortBy && sortOrder;

  const {
    data: examplePages,
    isLoading: areExamplesLoading,
    mutate: mutateExamplePages,
  } = useExampleRunsInfinite(
    EVALUATOR_PLAYGROUND_BATCH_SIZE,
    datasetId ?? null,
    shouldFetchExamplesWithRuns
      ? {
          session_ids: [sessionId],
          filters: { [sessionId]: ["eq(feedback_key, 'alignment')"] },
          sort_params:
            sortBy && sortOrder
              ? {
                  sort_by: sortBy,
                  sort_order: sortOrder,
                }
              : undefined,
          comparative_experiment_id: undefined,
          preview: true,
        }
      : null,
    undefined,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
      refreshInterval: isSessionFinished ? undefined : 10000,
    }
  );

  useEffect(() => {
    if (isSessionFinished) {
      // when the session finishes, we will immediately stop polling the examples. If the user has sorted the examples and the session finishes, we need one last refetch to make sure we don't miss any.
      // if the user has not sorted the examples, shouldFetchExamplesWithRuns will be false, so this will do nothing.
      mutateExamplePages().then(() => {
        setTimeout(() => {
          // stop highlighting examples when session has finished
          setShouldHighlightExamples(false);
        }, 1000);
      });
    }
  }, [isSessionFinished, mutateExamplePages]);

  useEffect(() => {
    if (!!sessionId && sortBy && sortOrder && !isSessionFinished) {
      setShouldHighlightExamples(true);
    }
  }, [sessionId, sortBy, sortOrder, isSessionFinished]);

  const datasetSplits = usePlaygroundStore((state) => state.datasetSplits);
  const examples = usePlaygroundStore((state) => state.examples);

  const { data: fetchedExamples } = useExamples(
    datasetId
      ? {
          dataset: datasetId ?? undefined,
          limit: EVALUATOR_PLAYGROUND_BATCH_SIZE,
          offset: 0,
          splits: getDatasetSplits(datasetSplits),
          select: [
            'id',
            'created_at',
            'modified_at',
            'name',
            'dataset_id',
            'source_run_id',
            'metadata',
            'inputs',
            'outputs',
            'attachment_urls',
          ],
          order: 'id',
          descending: false,
        }
      : null,
    undefined,
    {
      revalidateIfStale: true,
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (areExamplesLoading) {
      // this if statement prevents a visual flash when switching from descending sort to ascending sort -
      // for a second, the examples are undefined, so without this, we would switch back to the
      // fetchedExamples for a second.
      return;
    }
    const fetchedExamplesWithEdited = fetchedExamples?.map((e) => ({
      ...e,
      edited: false,
    }));
    if (examplePages && sessionId && sortBy && sortOrder) {
      const fetchedExampleRuns = examplePages.flatMap((e) =>
        e.map((ex) => ({ ...ex, edited: false }))
      );
      const mergedExampleRuns = fetchedExamplesWithEdited
        ? mergeExampleRuns(fetchedExampleRuns, fetchedExamplesWithEdited)
        : fetchedExampleRuns;
      setExamples(mergedExampleRuns);
    } else if (fetchedExamplesWithEdited) {
      setExamples(fetchedExamplesWithEdited);
    }
  }, [
    areExamplesLoading,
    examplePages,
    fetchedExamples,
    sessionId,
    setExamples,
    sortBy,
    sortOrder,
  ]);
  const _runsToFetch = usePlaygroundStore(
    useDeep((state) =>
      (
        state.prompts.flatMap((p) => p.runs?.map((r) => r.id) ?? []) ?? []
      ).filter(Boolean)
    )
  );
  // in the case where sorting is applied, we will fetch the examples and runs from the backend. However, the feedback might take some time to be generated/persisted. Thus, we should still fetch the
  // runs from the backend if they don't exist within the example runs. Or imagine if the ACE rule fails on one of the runs - we might never show it to the user (because only runs with feedback are returned
  // from the experiment view endpoint if a sort is applied).
  const runsToFetch = useMemo(() => {
    if (!shouldFetchExamplesWithRuns) {
      return _runsToFetch;
    }
    return _runsToFetch.filter(
      (r) => !examplePages?.[0]?.some((e) => e.runs?.some((rr) => rr.id === r))
    );
  }, [_runsToFetch, examplePages, shouldFetchExamplesWithRuns]);
  const variableMapping = useEvaluatorStore(
    useDeep((state) => state.variableMapping)
  );

  const runs = useRunsQuery(
    runsToFetch.length > 0
      ? {
          id: runsToFetch,
        }
      : null,
    undefined,
    {
      refreshInterval:
        numRunsFetched === runsToFetch.length && !needsFetchFeedbackKeys
          ? 0
          : 1000,
      onSuccess: (data) => {
        if (data.runs) {
          setNumRunsFetched(data.runs.length);
          const replacedRunsState = getPlaygroundStateWithReplacedFetchedRuns(
            prompts,
            data.runs
          );
          const allRuns = replacedRunsState?.flatMap((p) => p.runs ?? []);
          setNeedsFetchFeedbackKeys(
            needsToFetchFeedbackKeys(allRuns, evaluatorFeedbackKeys)
          );
        }
      },
    }
  );
  const prompts = usePlaygroundStore(useDeep((state) => state.prompts));
  const input = useInput();
  const isCreatingNewDataset = usePlaygroundStore(
    (state) => state.isCreatingNewDataset
  );

  const exampleRuns = useMemo(() => {
    const fetchedExampleRuns:
      | ExampleSchemaWithRunsAndOptionalFields[]
      | undefined = examplePages?.[0];
    const constructedExampleRuns =
      getExampleRunsFromPlaygroundState({
        isCreatingNewDataset,
        prompts,
        examples,
        datasetId,
        fetchedRuns: runs.data?.runs.map(formatRunOutput),
        input,
        variableMapping: variableMapping ?? undefined,
        extraReservedNames: ['few_shot_examples'],
      }) ?? [];
    let examplesToReturn = constructedExampleRuns;
    if (shouldFetchExamplesWithRuns && fetchedExampleRuns) {
      // merge the fetched example runs (experiment view endpoint) with the runs that were constructed from the fetched examples (normal example endpoint).
      examplesToReturn = mergeExampleRuns(
        fetchedExampleRuns,
        constructedExampleRuns
      );
    }
    return examplesToReturn;
  }, [
    examplePages,
    isCreatingNewDataset,
    prompts,
    examples,
    datasetId,
    runs.data?.runs,
    input,
    variableMapping,
    shouldFetchExamplesWithRuns,
  ]);

  return {
    exampleRuns,
    shouldHighlightExamples:
      shouldHighlightExamples &&
      loadingState !== 'loading' &&
      !!sortBy &&
      !!sortOrder,
  };
};
